import React, { useEffect, useState } from 'react'
import useScrollToTop from '../../hooks/useScrollToTop'
import { Grid, GridColumn, GridRow } from 'semantic-ui-react'
import { useSelector } from 'react-redux'
import REQUEST_STATUS from '../../state/common/REQUEST_STATUS'
import Loader from '../common/Loader'
import { GROUP_STATUS } from '../../constant/GROUP_STATUS'
import { useNavigate } from 'react-router-dom'
import ErrorAlert from '../common/ErrorAlert'
import _ from 'lodash'
import { DECISIONS, DECISIONS_DISPLAY_NAME } from '../../constant/DECISIONS'
import { DECISION_REASONS } from '../../constant/DECISION_REASONS'
import {
  SHOW_PRE_DECISIONS,
  DECISION_MESSAGES,
  EMAIL_TO_COME,
  FURTHER_VERIFICATION_TO_COME,
  MAILED_DETAILS_TO_COME,
  STRAIGHT_THROUGH_DECISION,
  THANK_YOU
} from '../../constant/DECISION_MESSAGES'
import {
  CONTACT_EMPLOYER,
  EOIUD,
  MOO,
  NON_PRIORITY_ONE,
  PRIORITY_ONE,
  SUCCESSFULLY_COMPLETED
} from '../../constant/PRIORITY_ONE_MESSAGES'
import FormDownloadLink from '../form/common/FormDownloadLink'

export const DECISION_TABLE_COLUMNS = [
  { header: 'Applicant', cellRender: x => _.capitalize(x.applicantType) },
  { header: 'Product', cellRender: x => x.decision.productType },
  { header: 'Decision', cellRender: x => DECISIONS_DISPLAY_NAME[x.decision.straightThroughDecision] },
  { header: 'Reason', cellRender: x => _.get(DECISION_REASONS, x.decision.reasonCode, x.decision.reasonCode) },
]

export default function Decision () {
  const { status, error, data } = useSelector(state => state.decision)
  const firstName = useSelector(state => state.form.segments.employee.fields.firstName.value)
  const formReady = useSelector(state => state.form.segments.verification.fields.ready.value)
  const [hideError, setHideError] = useState()
  const navigate = useNavigate()

  useEffect(() => {
    if (formReady !== 'true') {
      navigate('/form')
    }
  }, [formReady, navigate])

  const isPriorityOne = () => {
    const date = new Date()

    const isMonthInRange = date.getMonth() >= 10 || date.getMonth() <= 2
    const isDayInRange = date.getDate() >= 1 && date.getDate() <= 31
    const isNov1st = date.getDate() === 1 && date.getMonth() === 10

    return isMonthInRange && isDayInRange && !isNov1st
  }

  const pluralSuffix = Object.keys(data.documents)
    .filter(path => data.documents[path]?.path)
    .length > 1 ? 's' : ''

  const renderGroupStatusInProgressNotice = () => {
    return <GridColumn width={16}>
      <p>{THANK_YOU
        .replace(/!--firstName--!/, firstName)
        .replace(/!--pluralSuffix--!/g, pluralSuffix)}
      </p>
      <p>{EMAIL_TO_COME}</p>
    </GridColumn>
  }

  const renderCompletion = () => {
    return <>
      <GridColumn width={16}>
        <p>{SUCCESSFULLY_COMPLETED}</p>
        {isPriorityOne() ?
          <p>{PRIORITY_ONE}</p> :
          <p>{NON_PRIORITY_ONE}</p>}
        <p>{CONTACT_EMPLOYER}</p>
        <p>{EOIUD}</p>
        <p>{MOO}</p>
      </GridColumn>
    </>
  }

  const renderDecisions = () => {
    if (data.straightThroughDecision) {
      return <>
        <GridColumn width={16}>
          {STRAIGHT_THROUGH_DECISION.replace(/!--pluralSuffix--!/g, pluralSuffix)}
          <table className='Table'>
            <thead>
            <tr>
              {DECISION_TABLE_COLUMNS.map(c => <th key={c.header}>{c.header}</th>)}
            </tr>
            </thead>
            <tbody>
            {data.straightThroughUnderwritingDecisions.map((d, i) => {
              return <tr key={i}>
                {DECISION_TABLE_COLUMNS.map(c => {
                  return <td key={c.header}>{c.cellRender(d)}</td>
                })}
              </tr>
            })}
            </tbody>
          </table>
        </GridColumn>
        <GridColumn width={16} className='u-mY2'>
          <p>{MAILED_DETAILS_TO_COME}</p>
        </GridColumn>
      </>
    } else {
      return <GridColumn width={16}>
        <p>
          {_.get(DECISION_MESSAGES, data.decision, DECISION_MESSAGES.DEFAULT)
            .replace(/!--firstName--!/g, firstName)
            .replace(/!--pluralSuffix--!/g, pluralSuffix)
          }
        </p>
        <p>{EMAIL_TO_COME}</p>
        {data.decision === DECISIONS.PRE_APPROVED && <p>{FURTHER_VERIFICATION_TO_COME}</p>}
      </GridColumn>
    }
  }

  return useScrollToTop(<>
    {[REQUEST_STATUS.NOT_STARTED, REQUEST_STATUS.IN_PROGRESS].includes(status) && <Loader
      message='Thank you for your submission. Your application is being evaluated, and may take up to 3 minutes' />}
    {[REQUEST_STATUS.SUCCEEDED, REQUEST_STATUS.FAILED].includes(status) && <Grid className='container-md'>
      <GridRow>
        <GridColumn width={16}>
          <h3>Submission Complete</h3>
        </GridColumn>
      </GridRow>
      <GridRow className='horizontally-divided'>
        {(status === REQUEST_STATUS.FAILED && error && !hideError) && <ErrorAlert
          id='submission-error'
          content='An EOI submission error has occurred'
          onClose={() => setHideError(true)}
        />}
        {data.groupStatus === GROUP_STATUS.IN_PROGRESS ?
          renderGroupStatusInProgressNotice() :
          (SHOW_PRE_DECISIONS ? renderDecisions() : renderCompletion())
        }
        <GridColumn width={16} className='u-mY2'>
          {Object.keys(data.documents).filter(k => !_.isEmpty(data.documents[k])).map(form => {
            return <FormDownloadLink
              key={form}
              formType={form}
              fileName={data.documents[form].path}
            />
          })}
        </GridColumn>
        <GridColumn width={16}>
          <p><strong>Important</strong>: Save the final form{pluralSuffix} for your records
            by downloading or printing the file{pluralSuffix} above.</p>
        </GridColumn>
      </GridRow>
    </Grid>}
  </>)
}